import { useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronsUpDown, ChevronsDownUp } from 'lucide-react';
import { KanbanColumn } from './KanbanColumn';
import { KanbanCard } from './KanbanCard';
import { StageChangeDialog } from './StageChangeDialog';
import {
  updateApplicationStage,
  type PipelineApplication,
  type PipelineData,
} from '@/lib/api';
import { PIPELINE_STAGES, STAGE_LABELS, type PipelineStage } from '@/types';
import { useToast } from '@/contexts/ToastContext';

interface KanbanBoardProps {
  pipelineData: PipelineData;
  isLoading?: boolean;
}

// Stages a candidate may not be dragged OUT of. Mirrors the backend LOCKED_STAGES:
// `disqualified` is locked (re-engage via a new application); `hired` is movable so
// a hire that falls through can be walked back to an earlier stage.
const LOCKED_STAGES: PipelineStage[] = ['disqualified'];

// Collapse-all-cards preference persists across visits.
const COLLAPSE_KEY = 'opus.pipeline.cardsCollapsed';

export function KanbanBoard({ pipelineData, isLoading }: KanbanBoardProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Track the currently dragging application
  const [activeApplication, setActiveApplication] = useState<PipelineApplication | null>(null);

  // Collapse cards to compact one-line rows (persisted)
  const [cardsCollapsed, setCardsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleCardsCollapsed = () =>
    setCardsCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // localStorage unavailable — preference just won't persist
      }
      return next;
    });

  // Stage change dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    application: PipelineApplication;
    fromStage: PipelineStage;
    toStage: PipelineStage;
  } | null>(null);

  // Horizontal scroll state for fade indicators + chevron buttons
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState({ canLeft: false, canRight: false });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setScrollState({
      canLeft: el.scrollLeft > 4,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, pipelineData.stages]);

  const scrollByColumn = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;

    const distance = Math.max(260, Math.floor(el.clientWidth * 0.75));
    el.scrollBy({ left: direction * distance, behavior: 'smooth' });
    window.setTimeout(updateScrollState, 350);
  };

  const scrollToStage = (stage: PipelineStage) => {
    const stageColumn = scrollRef.current?.querySelector<HTMLElement>(`[data-stage-column="${stage}"]`);
    stageColumn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    window.setTimeout(updateScrollState, 350);
  };

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Mutation for updating application stage
  const updateStageMutation = useMutation({
    mutationFn: ({
      applicationId,
      stage,
      notes,
      disqualificationReason,
    }: {
      applicationId: string;
      stage: string;
      notes?: string;
      disqualificationReason?: string;
    }) => updateApplicationStage(applicationId, { stage, notes, disqualificationReason }),
    onMutate: async ({ applicationId, stage }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['pipeline'] });

      // Snapshot the previous pipeline data for rollback
      const previousData = queryClient.getQueryData<PipelineData>(['pipeline', pipelineData.jobId]);

      // Optimistically update the cache — move card from old stage to new stage
      if (previousData) {
        const newStages = { ...previousData.stages };
        let movedApp: PipelineApplication | undefined;

        // Remove from old stage
        for (const stageName of PIPELINE_STAGES) {
          const apps = newStages[stageName] || [];
          const idx = apps.findIndex((app) => app.id === applicationId);
          if (idx !== -1) {
            movedApp = { ...apps[idx], currentStage: stage as PipelineStage };
            newStages[stageName] = apps.filter((_, i) => i !== idx);
            break;
          }
        }

        // Add to new stage
        if (movedApp) {
          newStages[stage] = [...(newStages[stage] || []), movedApp];
        }

        queryClient.setQueryData<PipelineData>(['pipeline', pipelineData.jobId], {
          ...previousData,
          stages: newStages,
        });
      }

      return { previousData };
    },
    onSuccess: () => {
      // Invalidate pipeline data to refetch as a fallback
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      setDialogOpen(false);
      setPendingMove(null);
    },
    onError: (error, _variables, context) => {
      console.error('Failed to update application stage:', error);
      // Rollback to the previous data
      if (context?.previousData) {
        queryClient.setQueryData(['pipeline', pipelineData.jobId], context.previousData);
      }
      addToast({
        type: 'error',
        title: 'Stage change failed',
        message: 'Could not update the application stage. Please try again.',
      });
    },
  });

  // Find which stage an application belongs to
  const findApplicationStage = useCallback(
    (applicationId: string): PipelineStage | null => {
      for (const stage of PIPELINE_STAGES) {
        const applications = pipelineData.stages[stage] || [];
        if (applications.some((app) => app.id === applicationId)) {
          return stage;
        }
      }
      return null;
    },
    [pipelineData.stages],
  );

  // Find application by ID
  const findApplication = useCallback(
    (applicationId: string): PipelineApplication | null => {
      for (const stage of PIPELINE_STAGES) {
        const applications = pipelineData.stages[stage] || [];
        const found = applications.find((app) => app.id === applicationId);
        if (found) return found;
      }
      return null;
    },
    [pipelineData.stages],
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const application = findApplication(active.id as string);
    setActiveApplication(application);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveApplication(null);

    if (!over) return;

    const applicationId = active.id as string;
    const application = findApplication(applicationId);
    if (!application) return;

    const fromStage = findApplicationStage(applicationId);
    if (!fromStage) return;

    // Determine the target stage
    let toStage: PipelineStage | null = null;

    // Check if dropped on a column
    if (over.data.current?.type === 'column') {
      toStage = over.id as PipelineStage;
    }
    // Check if dropped on another application (get its column)
    else if (over.data.current?.type === 'application') {
      toStage = findApplicationStage(over.id as string);
    }
    // Check if dropped directly on stage ID
    else if (PIPELINE_STAGES.includes(over.id as PipelineStage)) {
      toStage = over.id as PipelineStage;
    }

    if (!toStage || fromStage === toStage) return;

    // Block dragging out of a locked stage (disqualified). Hired is movable.
    if (LOCKED_STAGES.includes(fromStage)) {
      addToast({
        type: 'warning',
        title: 'Cannot move candidate',
        message: `Candidates in the "${STAGE_LABELS[fromStage]}" stage can't be moved. Re-engage them via a new application if needed.`,
      });
      return;
    }

    // If moving to disqualified, show confirmation dialog
    if (toStage === 'disqualified') {
      setPendingMove({ application, fromStage, toStage });
      setDialogOpen(true);
    } else {
      // For non-disqualified moves, show confirmation dialog as well
      // This gives users a chance to add notes
      setPendingMove({ application, fromStage, toStage });
      setDialogOpen(true);
    }
  };

  // Handle stage change confirmation
  const handleStageChangeConfirm = (notes: string, disqualificationReason?: string) => {
    if (!pendingMove) return;

    updateStageMutation.mutate({
      applicationId: pendingMove.application.id,
      stage: pendingMove.toStage,
      notes: notes || undefined,
      disqualificationReason,
    });
  };

  // Handle dialog close
  const handleDialogClose = (open: boolean) => {
    if (!open && !updateStageMutation.isPending) {
      setDialogOpen(false);
      setPendingMove(null);
    }
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="relative flex h-full flex-col">
          <div className="mb-3 flex flex-shrink-0 flex-col gap-3 rounded-lg border border-white/10 bg-bg-secondary/70 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-text-primary">Pipeline navigation</p>
                <p className="text-xs text-text-tertiary">
                  Use these controls to move the board left and right. They do not move candidates.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={toggleCardsCollapsed}
                  aria-pressed={cardsCollapsed}
                  className="flex items-center gap-1.5 rounded-md border border-white/10 bg-bg-tertiary px-3 py-1.5 text-sm text-text-secondary transition hover:border-white/20 hover:text-text-primary"
                >
                  {cardsCollapsed ? (
                    <ChevronsUpDown className="h-4 w-4" />
                  ) : (
                    <ChevronsDownUp className="h-4 w-4" />
                  )}
                  {cardsCollapsed ? 'Expand cards' : 'Collapse cards'}
                </button>
                <button
                  type="button"
                  onClick={() => scrollByColumn(-1)}
                  disabled={!scrollState.canLeft}
                  className="rounded-md border border-white/10 bg-bg-tertiary px-3 py-1.5 text-sm text-text-secondary transition hover:border-white/20 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ← Previous columns
                </button>
                <button
                  type="button"
                  onClick={() => scrollByColumn(1)}
                  disabled={!scrollState.canRight}
                  className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-300 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next columns →
                </button>
                <button
                  type="button"
                  onClick={() => scrollToStage('disqualified')}
                  className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-sm font-medium text-red-300 transition hover:bg-red-500/20"
                >
                  View Disqualified
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-text-tertiary">Jump to:</span>
              {PIPELINE_STAGES.map((stage) => {
                const count = pipelineData.stages[stage]?.length || 0;
                return (
                  <button
                    key={stage}
                    type="button"
                    onClick={() => scrollToStage(stage)}
                    className="flex items-center gap-1 rounded-full border border-white/10 bg-bg-tertiary px-2.5 py-1 text-text-secondary transition hover:border-white/20 hover:bg-bg-elevated hover:text-text-primary"
                  >
                    <span>{STAGE_LABELS[stage]}</span>
                    <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-text-tertiary">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative min-h-0 flex-1">
            <div
              ref={scrollRef}
              className="flex gap-4 h-full overflow-x-auto pb-4 kanban-scroll"
            >
              {PIPELINE_STAGES.map((stage) => (
                <div key={stage} data-stage-column={stage} className="h-full flex-shrink-0">
                  <KanbanColumn
                    stage={stage}
                    applications={pipelineData.stages[stage] || []}
                    isLoading={isLoading}
                    collapsed={cardsCollapsed}
                  />
                </div>
              ))}
            </div>

            {/* Left/right board navigation. Buttons stay visible so users know the board scrolls horizontally. */}
            {scrollState.canLeft && (
              <div
                aria-hidden
                className="pointer-events-none absolute left-0 top-0 bottom-4 w-10 bg-gradient-to-r from-bg-primary via-bg-primary/70 to-transparent"
              />
            )}
            <button
              type="button"
              aria-label="Scroll pipeline left"
              onClick={() => scrollByColumn(-1)}
              disabled={!scrollState.canLeft}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-bg-secondary/90 border border-white/10 text-text-primary shadow-lg transition hover:bg-bg-tertiary disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {scrollState.canRight && (
              <div
                aria-hidden
                className="pointer-events-none absolute right-0 top-0 bottom-4 w-10 bg-gradient-to-l from-bg-primary via-bg-primary/70 to-transparent"
              />
            )}
            <button
              type="button"
              aria-label="Scroll pipeline right"
              onClick={() => scrollByColumn(1)}
              disabled={!scrollState.canRight}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-bg-secondary/90 border border-white/10 text-text-primary shadow-lg transition hover:bg-bg-tertiary disabled:opacity-35 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Drag Overlay - shows dragged card above everything */}
        <DragOverlay>
          {activeApplication ? (
            <KanbanCard application={activeApplication} isDragging collapsed={cardsCollapsed} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Stage Change Confirmation Dialog */}
      <StageChangeDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        application={pendingMove?.application || null}
        fromStage={pendingMove?.fromStage || null}
        toStage={pendingMove?.toStage || null}
        onConfirm={handleStageChangeConfirm}
        isLoading={updateStageMutation.isPending}
      />
    </>
  );
}
